// helper to check if coupon targets no specific plans/items
const hasNoSpecificTargets = coupon =>
  !(coupon?.appliesToPlans?.length > 0) && !(coupon?.appliesToItems?.length > 0)

const couponAppliesDirectly = (coupon, curr) => {
  if (curr.prodType === 'plan') {
    if (!(coupon.appliesToItems && coupon.appliesToItems.length > 0)) {
      return coupon.appliesToPlans?.includes(curr?.code)
    } else {
      return false
    }
  }
  return coupon.appliesToItems?.includes(curr?.code)
}

// helper to check if coupon applies by type (plan, oneTimeFee, itemBasedAddon)
const couponAppliesByType = (coupon, curr) => {
  if (hasNoSpecificTargets(coupon)) {
    if (coupon.appliesToAllPlans && curr?.prodType === 'plan') return true
    // this is applied if the coupon applies to all plans,
    // and the current item is an addon (part of a plan, we know this by the presence of planCode)
    if (
      coupon.appliesToAllPlans &&
      curr?.prodType === 'itemBasedAddon' &&
      !!curr?.planCode
    )
      return true
    if (coupon.appliesToNonPlanCharges && curr?.prodType === 'oneTimeFee')
      return true
    if (coupon.appliesToAllItems && curr?.prodType === 'itemBasedAddon')
      return true
  }
  return false
}

/**
 * Calculate total coupon discount amount that applies to a single item.
 *
 * @param {{code: string, prodType: 'plan'|'oneTimeFee'|'itemBasedAddon', currency: {unitAmount: number}, quantity?: number}} itemDetails
 * @param {Array<{discountAmount?: number, discountPercent?: number, appliesToPlans?: string[], appliesToItems?: string[], appliesToAllPlans?: boolean, appliesToNonPlanCharges?: boolean, appliesToAllItems?: boolean}>} coupons
 * @param {number} [gstMultiplier=1] Gross-up factor for fixed-amount coupons in
 *   GST-inclusive markets (AU 1.1). Recurly returns fixed coupon
 *   `discountAmount` tax-exclusive, but AU prices are displayed GST-inclusive,
 *   so the fixed discount must be grossed up to match. Percentage coupons are
 *   left untouched — they already derive from the GST-inclusive `unitAmount`.
 *
 * Stacking (FULSAS-13837): among coupons of the same kind only the best one
 * wins, but a fixed-amount coupon and a percent coupon on the same item stack,
 * fixed first and then the percent on what remains (e.g. AU Social Ads Bronze
 * 825 - 250 = 575, then 75% off = 143.75). `coupon` is still the single
 * largest standalone coupon so existing callers keep their coupon label.
 * @returns {{discountAmount: number, coupon: {discountAmount?: number, discountPercent?: number, appliesToPlans?: string[], appliesToItems?: string[], appliesToAllPlans?: boolean, appliesToNonPlanCharges?: boolean, appliesToAllItems?: boolean}}}
 */
export const calculateCouponsAmountForItem = (
  itemDetails,
  coupons,
  gstMultiplier = 1,
) => {
  let bestAmount = 0
  let appliedCoupon = null
  let bestFixed = 0
  let bestPercent = 0
  const qty = itemDetails.quantity || 1
  const unit = itemDetails.currency?.unitAmount || 0
  const base = qty * unit

  for (const coupon of coupons) {
    let couponAmount = 0
    if (
      couponAppliesDirectly(coupon, itemDetails) ||
      couponAppliesByType(coupon, itemDetails)
    ) {
      if (coupon.discountAmount > 0) {
        couponAmount = coupon.discountAmount * gstMultiplier
        bestFixed = Math.max(bestFixed, couponAmount)
      } else if (coupon.discountPercent > 0) {
        couponAmount = base * (coupon.discountPercent / 100)
        bestPercent = Math.max(bestPercent, coupon.discountPercent)
      }
    }
    if (couponAmount > bestAmount) {
      bestAmount = couponAmount
      appliedCoupon = coupon
    }
  }

  let productDiscountAmount = bestAmount
  if (bestFixed > 0 && bestPercent > 0 && base > 0) {
    const fixedApplied = Math.min(bestFixed, base)
    productDiscountAmount =
      fixedApplied + (base - fixedApplied) * (bestPercent / 100)
  }

  return {
    discountAmount: Math.max(productDiscountAmount, 0),
    coupon: appliedCoupon,
  }
}
